import React, { FC } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';

import { Texto } from 'view/component/Tipografia/Texto';
import { ListaFiltros } from './ListaFiltros';
import { PrioridadeTag } from 'view/component/Prioridade/PrioridadeTag';
import { Prioridades } from 'model/solicitacao/PrioridadeSolicitacao';
import { SituacaoTag } from 'view/component/Situacao/SituacaoTag';
import { Situacoes } from 'model/solicitacao/Situacao';
import { Data } from 'view/component/Tempo/Data';
import { centralizadoVerticalmente } from 'styles/Utilitarios';
import { AvatarUnidadeById } from 'view/component/AvatarUnidade/AvatarUnidade';
import { AvatarPessoaPesquisavel } from 'view/component/AvatarPessoa/AvatarPessoaPesquisavel';
import { useSelector } from 'react-redux';
import { UnidadesResponsaveisSelector } from 'store/UnidadesResponsaveis/UnidadesResponsaveis.selector';
import Avatar from 'view/component/Avatar/Avatar';
import { AriaSort, OrdemColunaTabela } from 'view/component/Tabela/model/OrdemColunaTabela';
import { DetalhesFiltro } from './FiltroResumo/DetalhesFiltro';
import { FiltroResumoItem, ItemResumoVazio } from './FiltroResumo/FiltroResumoItem';
import { FiltroResumoItemOrdem } from './FiltroResumo/FiltroResumoItemSort';
import { SolicitacoesQueryParams } from 'service/api/solicitacao/ApiSolicitacoes';
import { ClassificacaoCascaderStyled } from 'view/component/ClassificacaoCascader/ClassificacaoCascader';
import { Formulario } from 'view/component/Formulario/Formulario';
import { OperacaoFormularioBusca } from 'service/restricao/OperacaoFormulario/OperacaoFormulario';
import { ClassificacaoSelector } from 'store/Classificacao/Classificacao.selector';
import { buildOptions, CascadeOptions, getDefaultValue } from 'view/component/CascaderField/CascaderField';
import { Classificacao } from 'model/classificacao/Classificacao';


export interface SolicitacoesFiltrosResumoProps {
  onFiltrosAtualizados: (values: SolicitacoesQueryParams) => void;
  onOrdemAtualizada: (values?: OrdemColunaTabela) => void;
  filtros: SolicitacoesQueryParams;
  ordem?: OrdemColunaTabela;
}


export const SolicitacoesFiltrosResumo: FC<SolicitacoesFiltrosResumoProps> = ({filtros, ordem, onFiltrosAtualizados, onOrdemAtualizada}) => {
  const classificacoes = useSelector(ClassificacaoSelector.lista.elementos);

  const lista = new ListaFiltros();
  const classificacoesAsOptions = buildOptions(classificacoes);

  const detalhesFiltro: DetalhesFiltro[] = [
    {rotulo: "Título", identificador: "titulo", tratamento: tratamentoTexto()},
    {rotulo: "Prioridade", identificador: "idPrioridadeSolicitacao", tratamento: tratamentoPrioridades()},
    {rotulo: "Situação", identificador: "idSituacao", identificadorOrdem: "situacao.nome", tratamento: tratamentoSituacao()},
    {rotulo: "Criado Por", identificador: "idCriador", tratamento: tratamentoUsuarios()},
    {rotulo: "Solicitado Por", identificador: "idSolicitante", tratamento: tratamentoUsuarios()},
    {rotulo: "Responsável técnico", identificador: "idResponsavelTecnico", tratamento: tratamentoMembrosUnidade()},
    {rotulo: "Interessado", identificador: "idInteressado", tratamento: tratamentoUsuarios()},
    {rotulo: "Unidade Responsável", identificador: "idUnidadeResponsavel", identificadorOrdem: "unidadeResponsavel.nome", tratamento: tratamentoUnidades()},
    {rotulo: "Data criação", identificador: "dataCriacaoInicio", tratamento: tratamentoData()},
    {rotulo: "Data criação fim", identificador: "dataCriacaoFim", tratamento: tratamentoData()},
    {rotulo: "Data atualização", identificador: "dataAtualizacao", tratamento: tratamentoData()},
    {rotulo: "Classificação", identificador: "idClassificacao", identificadorOrdem: "classificacao.nome", tratamento: tratamentoClassificacao(classificacoesAsOptions)},
    {rotulo: "Número da solicitação", identificador: "id", tratamento: tratamentoNumeroSolicitacao()},
  ];

  const removerFiltro = (identificador: keyof SolicitacoesQueryParams) => {
    const filtroAtualizado = {...filtros};
    delete filtroAtualizado[identificador];
    onFiltrosAtualizados(filtroAtualizado);
  }
  const removerOrdem = () => onOrdemAtualizada(undefined);

  detalhesFiltro.map(
    (detalhe, indice) => 
      lista.add(<FiltroResumoItem
        key={indice}

        filtros={filtros}
        rotulo={detalhe.rotulo}
        onRemoverClick={removerFiltro}
        identificador={detalhe.identificador}
        tratamento={detalhe.tratamento}
      />)
  );

  const algumFiltroAplicado = Object.keys(filtros ?? {}).length !== 0;
  const algumaOrdemAplicada = ordem !== undefined && ordem.ordem !== AriaSort.None;

  return (
    <Container>
      <Texto>Filtrado por:</Texto>
      <Itens>
        {!algumFiltroAplicado && !algumaOrdemAplicada && <ItemResumoVazio>Nenhum filtro aplicado</ItemResumoVazio>}
        {lista.elementos}
        {algumaOrdemAplicada && <FiltroResumoItemOrdem 
          onRemoverClick={removerOrdem}
          detalhesFiltro={detalhesFiltro}
          ordem={ordem} />
        }
      </Itens>
      
    </Container>
  )
}

const Container = styled.aside`
  ${centralizadoVerticalmente};
  width: 100%;
`;

const Itens = styled.div`
  ${centralizadoVerticalmente};
  overflow-x: auto;
`;

function tratamentoPrioridades(): (elementos: any|any[]) => any {
  return e => e.map((id: number) => <PrioridadeTag key={id} prioridade={Prioridades.byId(id)} exibirIcone={true} />);
}

function tratamentoSituacao(): (elementos: any|any[]) => any {
  return e => e.map((id: number) => <SituacaoTag key={id} situacao={Situacoes.byId(id)} />);
}

function tratamentoUsuarios(): (elementos: any|any[]) => any {
  return e => e.map((id: string) => <AvatarPessoaPesquisavel key={id} idInstitucional={id} exibirNome={false} comLink={false} />);
}

function tratamentoMembrosUnidade(): (elementos: any|any[]) => any {
  return e => e.map((id: number) => <AvatarMembroUnidade key={id} id={id} />);
}

interface AvatarMembroUnidadeProps {
  id: number;
}

const AvatarMembroUnidade: FC<AvatarMembroUnidadeProps> = ({id}) => {
  const membroUnidade = useSelector(UnidadesResponsaveisSelector.responsavelTecnico(id));
  const isCarregando = useSelector(UnidadesResponsaveisSelector.isCarregando);

  return <Avatar loading={isCarregando} aria-hidden={true} nome={membroUnidade?.nome ?? ''} />
}

function tratamentoUnidades(): (elementos: any|any[]) => any {
  return e => e.map((id: number) => <AvatarUnidadeById key={id} idUnidade={id} />);
}

function tratamentoData(): (elementos: any|any[]) => any {
  return e => e.map((dataString: string) => {
    const data = dayjs(dataString)
    return (<>
      &ge;
      <Data data={data} />
      </>
  )});
}

function tratamentoTexto(): (elementos: string[]) => any {
  return e => e.map((titulo: string) => <Texto>{titulo}</Texto>);
}

function tratamentoNumeroSolicitacao(): (elementos: any|any[]) => any {
  return e => e.map((id: string) => <><Texto>#{id}</Texto></>);
}

function tratamentoClassificacao(options: CascadeOptions<Classificacao>): (elementos: any|any[]) => any {
  return e => {
    return e.map((id: number) => {
      const valor = getDefaultValue(options, id, e => e.label);
      return <Texto>{valor.join(' > ')}</Texto>
    });
  };
}